import { useCallback, useEffect, useState } from 'react'
// Material
import { Stack, Typography, Avatar, Box, Button } from '@mui/material'
import { styled } from '@mui/material/styles'
import { formatFileSize, getFileTitle } from '$func/file'
import { AudioTypeIcon } from '$common/icons/file-types/audio'
import { getAudioDuration } from '$func/audio-duration'
import { toLocale } from '$func/text'
import { t } from '$i18n'

const Root = styled(Stack)`
  box-sizing: border-box;
  padding: 10px 10px 10px 2px;
  overflow: hidden;
  min-height: 70px;
  background-color: white;
  z-index: 1;
`
const InfoContainer = styled(Stack)`
  width: 100%;
  justify-content: center;
  margin-right: 15px;
  overflow: hidden;
`

const StyledSecondaryText = styled(Typography)`
  direction: rtl;
`

interface Props {
  file: File
  onCancel: () => unknown
  hasBorder: boolean
}

export function AudioPreview({ file, onCancel, hasBorder }: Props) {
  if (!file) return null
  const [formattedSeconds, setFormattedSeconds] = useState('')

  const avatarSrc = <AudioTypeIcon /> // TODO: merge this component with file-preview

  useEffect(() => {
    getAudioDuration(file).then(formatSeconds)
  }, [file])

  const formatSeconds = useCallback(({ duration }: { duration: number }) => {
    const durationFloor = Math.floor(duration / 1000)
    const seconds = toLocale(durationFloor % 60)
      .toString()
      .padStart(2, toLocale(0).toString())
    const minutes = toLocale((durationFloor - (durationFloor % 60)) / 60)
      .toString()
      .padStart(2, toLocale(0).toString())
    setFormattedSeconds(`${minutes}:${seconds}`)
  }, [])

  return (
    <Root
      flexDirection="row"
      sx={{ borderRadius: hasBorder ? '8px 8px 0 0' : 'unset' }}
    >
      <Avatar sx={{ width: 50, height: 50, backgroundColor: 'veryLightGray' }}>
        {avatarSrc}
      </Avatar>
      <InfoContainer>
        <Typography variant="caption1" component="div" noWrap>
          {getFileTitle(file.name)}
        </Typography>
        <Box sx={{ display: 'flex', marginTop: '8px' }}>
          <StyledSecondaryText variant="caption1" color="gray">
            {formattedSeconds}
          </StyledSecondaryText>
          <StyledSecondaryText
            variant="caption1"
            color="gray"
            sx={{ margin: '0 2px 0 4px' }}
          >
            /
          </StyledSecondaryText>
          <StyledSecondaryText variant="caption1" color="gray" noWrap>
            {`${formatFileSize(file.size).sizeText}`}
          </StyledSecondaryText>
        </Box>
      </InfoContainer>
      <Button sx={{ color: 'error.main' }} onClick={onCancel}>
        {t('common.refuse')}
      </Button>
    </Root>
  )
}
